<template>
  <div id="app" v-on:mousemove="this.scheduleSessionTimeout">
    <div id="header">
      <img alt="Vyaire Concerto logo" src="./assets/header.png" />
    </div>
    <Navbar :routes="routes" :user="user" :logout="logout" :title="title" />
    <div id="content" class="container">
      <router-view :user="user" :routes="routes" :settings="settings" :set-title="(t) => (title = t)" />
    </div>
  </div>
</template>

<script>
import decode from "jwt-decode";

import Navbar from "./components/Navbar";
import EventBus from "./utils/event-bus";
import { clearToken, getToken, isLoggedIn, setToken } from "./utils/auth";
import CoreAPI from "./utils/core-api";
import { LOGIN_SUCCESS, LOGOUT, SESSION_TIMEOUT, SETTINGS_CHANGED } from "./utils/constants";
import debounce from "./utils/debounce";

export default {
  name: "app",
  data() {
    return {
      user: null,
      routes: [],
      settings: null,
      sessionTimer: 0,
      title: ""
    };
  },
  components: {
    Navbar
  },
  mounted() {
    EventBus.$on(LOGOUT, () => {
      clearToken();
      this.user = null;
      this.routes = [];
    });

    EventBus.$on(SESSION_TIMEOUT, () => {
      clearToken();
      this.user = null;
      this.routes = [];
      if (this.$route.path !== "/sign-in") {
        this.$router.push("/sign-in");
      }
    });

    EventBus.$on(LOGIN_SUCCESS, async data => {
      setToken(data.token, this.$nativeApp);
      const decodedToken = decode(data.token);
      this.user = decodedToken.user;
      this.routes = this.getRoutesFromClaims(decodedToken.user.claims);
      if (this.routes.length === 0) {
        this.$router.push("/no-access");
      } else {
        await this.getSettings();

        // This is called if the user refreshes the page, so only redirect if they are on the Sign In page
        if (this.$route.path === "/sign-in") {
          this.$router.push(this.routes[0].path);
        }

        this.scheduleSessionTimeout();
      }
    });

    EventBus.$on(SETTINGS_CHANGED, () => {
      this.getSettings();
    });

    if (isLoggedIn()) {
      EventBus.$emit(LOGIN_SUCCESS, { token: getToken() });
    }
    // Don't redirect the user if they are already on the sign-in page, doing so creates Vue errors in the console
    else if (this.$router.currentRoute.path !== "/sign-in") {
      this.$router.push("/sign-in");
    }
  },
  beforeDestroy() {
    clearInterval(this.sessionTimer);
  },
  computed: {
    scheduleSessionTimeoutWithDebounce() {
      return debounce(() => {
        // clear last timeout before setting new one
        clearInterval(this.sessionTimer);

        const timeout = this.settings.webSessionTimeout * 1000 * 60;
        this.sessionTimer = setTimeout(this.timeoutSession, timeout);
      }, 1000);
    }
  },
  methods: {
    scheduleSessionTimeout() {
      // Mobile users can't trigger the mousemove event, so only trigger the session keep-alive for Web users. Mobile
      // users will have their session expire after their last login plus the configured time Mobile session timeout.
      if (this.$nativeApp.isMobile) return;

      // If the user isn't logged in, don't bother checking their session and trying to log them out
      if (!isLoggedIn) return;

      if (this.settings) {
        this.scheduleSessionTimeoutWithDebounce();
      }
    },
    async timeoutSession() {
      // Sometimes the timer still fires when the user is already logged out. In these cases, don't bother calling the
      // logout API (because it will cause 401 errors). But it doesn't hurt to call the rest of the code to ensure the
      // user is fully logged out.
      if (!isLoggedIn()) return;

      await this.logout();
    },
    async logout() {
      try {
        await CoreAPI.logout();
      } catch (e) {
        // Ignore failed logout API calls since the user may have lost network connection and this just logs an event
        // in VVS. It's more important to clear out the user's JWT.
      }

      // Ensure the session timeout stops firing when logged out
      clearInterval(this.sessionTimer);

      clearToken();
      this.user = null;
      this.routes = [];
      if (this.$router.currentRoute.path !== "/sign-in") {
        this.$router.push("/sign-in");
      }
    },
    getRoutesFromClaims(claims) {
      // These routes will be checked in order and listed in the nav bar in this order
      const routes = [
        {
          route: { path: "/associate", name: "Associate" },
          claims: ["get:device", "get:patient", "get:settings", "action:associate", "action:manualEntry"]
        },
        {
          route: { path: "/info", name: "Info" },
          claims: ["get:info"]
        },
        {
          route: { path: "/connections", name: "Connections" },
          claims: ["get:connections"]
        },
        {
          route: { path: "/locations", name: "Locations" },
          claims: ["get:locations"]
        },
        {
          route: { path: "/events", name: "Events" },
          claims: ["get:events"]
        },
        {
          route: { path: "/devices", name: "Devices" },
          claims: ["get:devices"]
        },
        {
          route: { path: "/settings", name: "Settings" },
          claims: ["get:settings", "update:settings"]
        },
        {
          route: { path: "/about", name: "About" }
        }
      ];

      return routes.filter(r => !r.claims || r.claims.every(c => claims.indexOf(c) >= 0)).map(r => r.route);
    },
    async getSettings() {
      this.settings = await CoreAPI.getSettings();
    }
  }
};
</script>

<style lang="scss">
@import "./assets/bootstrap";

html {
  position: relative;
  min-height: 100%;
}

#app {
  // on-mouse-move only works when the mouse is moved over the element, so set the element to take up the entire height
  // of the window so that anywhere the mouse moves on the window triggers the session keep alive
  min-height: 100vh;
}

#header {
  background-color: $backgroundColor;
  text-align: center;

  img {
    margin: 15px;
    height: 50px;
  }
}

#content {
  margin-bottom: 15px;
  margin-top: 15px;
}

.table-borderless td,
.table-borderless th {
  border: 0;
}

.heading {
  color: $primaryColor;
  font-size: 1.2em;
  border-bottom: 1px solid $primaryColor;
  margin-bottom: 0.5rem;
}
</style>
